import api from '@shared/services/api';
import { 
  CalendarData, 
  CalendarEvent, 
  CreateCategoryData, 
  CreateEventData, 
  EventCategory, 
  UpdateCategoryData, 
  UpdateEventData 
} from '../types/calendar.types';

/**
 * Service for handling calendar-related API calls
 */
export const CalendarService = {
  /**
   * Get all calendar data (events and categories)
   */
  async getCalendarData(): Promise<CalendarData> {
    const response = await api.get<CalendarData>('/calendar');
    return response.data;
  },

  /**
   * Get a specific event
   */
  async getEvent(eventId: string): Promise<CalendarEvent> {
    const response = await api.get<{ event: CalendarEvent }>(`/calendar/events/${eventId}`);
    return response.data.event;
  },

  /**
   * Create a new event
   */
  async createEvent(data: CreateEventData): Promise<CalendarEvent> {
    // Ensure the dates are sent in ISO format
    const eventData = {
      ...data,
      startDate: data.startDate ? new Date(data.startDate).toISOString() : undefined,
      endDate: data.endDate ? new Date(data.endDate).toISOString() : undefined
    };
    const response = await api.post<{ event: CalendarEvent }>('/calendar/events', eventData);
    return response.data.event;
  },

  /**
   * Update an existing event
   */
  async updateEvent(eventId: string, data: UpdateEventData): Promise<CalendarEvent> {
    // Ensure the dates are sent in ISO format
    const eventData = {
      ...data,
      startDate: data.startDate ? new Date(data.startDate).toISOString() : undefined,
      endDate: data.endDate ? new Date(data.endDate).toISOString() : undefined
    };
    const response = await api.put<{ event: CalendarEvent }>(`/calendar/events/${eventId}`, eventData);
    return response.data.event;
  },

  /**
   * Delete an event
   */
  async deleteEvent(eventId: string): Promise<void> {
    await api.delete(`/calendar/events/${eventId}`);
  },

  /**
   * Get all categories
   */
  async getCategories(): Promise<EventCategory[]> {
    const response = await api.get<{ categories: EventCategory[] }>('/calendar/categories');
    return response.data.categories;
  },

  /**
   * Create a new category
   */
  async createCategory(data: CreateCategoryData): Promise<EventCategory> {
    const response = await api.post<{ category: EventCategory }>('/calendar/categories', data);
    return response.data.category;
  },

  /**
   * Update a category
   */
  async updateCategory(categoryId: string, data: UpdateCategoryData): Promise<EventCategory> {
    const response = await api.put<{ category: EventCategory }>(`/calendar/categories/${categoryId}`, data);
    return response.data.category;
  },

  /**
   * Delete a category
   */
  async deleteCategory(categoryId: string): Promise<void> {
    await api.delete(`/calendar/categories/${categoryId}`);
  }
};
